<section
  class="tc-properties tf-eigenschaften"
  data-testid="tf-eigenschaften"
  aria-labelledby="tf-eigenschaften-titel"
  [attr.aria-busy]="zustand() === 'laedt' ? 'true' : null"
>
  <h3 id="tf-eigenschaften-titel" class="tf-eigenschaften__titel">
    {{ 'testCases.form.properties.blockTitle' | translate }}
  </h3>

  @if (zustand() === 'fehler') {
    <div class="tf-eigenschaften__ladefehler" role="status">
      <span>{{ 'testCases.form.properties.listsLoadError' | translate }}</span>
      <button type="button" class="tf-eigenschaften__retry" data-testid="tf-eigenschaften-retry" (click)="laden()">
        {{ 'testCases.form.properties.retry' | translate }}
      </button>
    </div>
  }

  <div class="tf-eigenschaften__felder">
    @for (feld of auswahlFelder(); track feld.control) {
      @if (zustand() === 'laedt') {
        <div class="tf-eigenschaften__platzhalter" aria-hidden="true"></div>
      } @else {
        <div class="tf-feld" [class.tf-feld--fehler]="feld.fehler">
          <label class="tf-feld__label" [for]="feld.id">
            {{ feld.labelKey | translate }}
            @if (feld.requiredKey) {
              <span class="tf-feld__stern" aria-hidden="true">*</span>
            }
          </label>
          @if (zustand() === 'fehler') {
            <select
              class="tf-feld__auswahl"
              disabled
              [id]="feld.id"
              [attr.data-testid]="'tf-eigenschaften-' + feld.testid"
            >
              <option>{{ feld.aktuell }}</option>
            </select>
          } @else {
            <!-- Das Attribut braucht der Fokus auf das erste fehlerhafte Feld im Formular; die Bindung allein setzt es nicht. -->
            <select
              class="tf-feld__auswahl"
              [id]="feld.id"
              [formControlName]="feld.control"
              [attr.formcontrolname]="feld.control"
              [attr.data-testid]="'tf-eigenschaften-' + feld.testid"
              [attr.aria-required]="feld.requiredKey ? 'true' : null"
              [attr.aria-invalid]="feld.fehler ? 'true' : null"
              [attr.aria-describedby]="feld.fehler ? feld.fehlerId : feld.inaktiv ? feld.hinweisId : null"
            >
              @if (feld.inaktiv; as inaktiv) {
                <option [value]="inaktiv.key" disabled>
                  {{ inaktiv.label }} {{ 'testCases.form.properties.inactiveSuffix' | translate }}
                </option>
              } @else if (feld.optionen.length === 0) {
                <option value="" disabled>{{ 'testCases.form.properties.noValues' | translate }}</option>
              }
              @for (option of feld.optionen; track option.key) {
                <option [value]="option.key">{{ option.label }}</option>
              }
            </select>
          }
          <mat-icon class="tf-feld__pfeil" aria-hidden="true">expand_more</mat-icon>
          @if (zustand() === 'geladen' && feld.inaktiv) {
            <p
              class="tf-feld__hinweis"
              [id]="feld.hinweisId"
              [attr.data-testid]="'tf-eigenschaften-inaktiv-' + feld.testid"
            >
              {{ 'testCases.form.properties.inactiveHint' | translate }}
            </p>
          }
          @if (feld.fehler; as fehler) {
            <p class="tc-field-error" role="alert" [id]="feld.fehlerId">
              {{ fehler.key ? (fehler.key | translate) : fehler.text }}
            </p>
          }
        </div>
      }
    }

    <div class="tf-feld">
      <label class="tf-feld__label" for="tf-eigenschaften-prioritaet-feld">
        {{ 'testCases.form.properties.priority' | translate }}
      </label>
      <select
        id="tf-eigenschaften-prioritaet-feld"
        class="tf-feld__auswahl"
        formControlName="priority"
        data-testid="tf-eigenschaften-prioritaet"
      >
        @for (priority of priorityOptions; track priority) {
          <option [value]="priority">{{ 'testCases.priority.' + priority | translate }}</option>
        }
      </select>
      <mat-icon class="tf-feld__pfeil" aria-hidden="true">expand_more</mat-icon>
    </div>

    <div class="tf-feld" [class.tf-feld--fehler]="verantwortlicherFehler()">
      <app-person-select
        class="tc-field"
        formControlName="assigneeUserId"
        data-testid="tf-eigenschaften-verantwortlicher"
        [label]="'testCases.form.properties.assignee' | translate"
        [options]="personOptions()"
        [allowNone]="false"
        [required]="true"
        noneKey="testCases.form.properties.noAssignee"
      ></app-person-select>
      @if (verantwortlicherFehler(); as fehler) {
        <p class="tc-field-error" role="alert" id="tf-eigenschaften-verantwortlicher-fehler">
          {{ fehler.key ? (fehler.key | translate) : fehler.text }}
        </p>
      }
    </div>

    <app-person-multi-select
      class="tc-field"
      formControlName="reviewerIds"
      data-testid="tf-eigenschaften-reviewer"
      [label]="'testCases.form.properties.reviewers' | translate"
      [options]="personOptions()"
      placeholderKey="testCases.form.properties.addReviewer"
      removeKey="testCases.form.properties.removeReviewer"
    ></app-person-multi-select>

    <div class="tf-feld">
      <label class="tf-feld__label" for="tf-eigenschaften-version-feld">
        {{ 'testCases.form.properties.version' | translate }}
      </label>
      <input
        id="tf-eigenschaften-version-feld"
        class="tf-feld__eingabe"
        readonly
        data-testid="tf-eigenschaften-version"
        [value]="version() ?? ('testCases.form.properties.versionEmpty' | translate)"
      />
    </div>

    <div class="tf-feld">
      <label class="tf-feld__label" for="tf-eigenschaften-tags-feld">
        {{ 'testCases.form.properties.tags' | translate }}
      </label>
      <div class="tf-feld__chips" data-testid="tf-eigenschaften-tags">
        <mat-chip-grid
          #tagGrid
          [disabled]="readOnly()"
          [attr.aria-label]="'testCases.form.properties.tagsAriaLabel' | translate"
        >
          @for (tag of tags(); track tag) {
            <mat-chip-row [removable]="!readOnly()" (removed)="removeTag(tag)">
              {{ tag }}
              <button matChipRemove [attr.aria-label]="'testCases.form.properties.removeTag' | translate">
                <mat-icon aria-hidden="true">cancel</mat-icon>
              </button>
            </mat-chip-row>
          }
        </mat-chip-grid>
        <input
          id="tf-eigenschaften-tags-feld"
          class="tf-feld__chip-eingabe"
          [matChipInputFor]="tagGrid"
          [disabled]="readOnly()"
          [placeholder]="'testCases.form.properties.tagsPlaceholder' | translate"
          (matChipInputTokenEnd)="addTag($event)"
        />
      </div>
    </div>

    @for (field of jiraTicketFields; track field.controlName) {
      <div class="tf-feld">
        <label class="tf-feld__label" [for]="'tf-eigenschaften-' + field.controlName">
          {{ field.labelKey | translate }}
        </label>
        <input
          class="tf-feld__eingabe"
          [id]="'tf-eigenschaften-' + field.controlName"
          [formControlName]="field.controlName"
          [attr.data-testid]="field.controlName === 'storyJiraKey' ? 'tf-story-ticket' : null"
          [attr.maxlength]="fieldLengths.KEY_MAX"
          [placeholder]="field.placeholderKey | translate"
        />
      </div>
    }
  </div>
</section>
